import classnames from 'classnames'
import * as React from 'react'

import { Button, ButtonHTMLProps, ButtonProps, ButtonTheme, ButtonThemeType } from '../button'
import { Delayed } from '../delayed'
import { Icon } from '../icon'
import { PopoverStateReturn, PopoverTrigger } from '../popover'
import { Spinner } from '../spinner'

function getButtonProps(theme: ButtonTheme, active: boolean): Omit<ButtonProps & ButtonHTMLProps, 'as'> {
  switch (theme) {
    case ButtonTheme.light:
      return {
        theme: 'transparent',
        contentClass: 'font-normal truncate',
        className: classnames(
          'border bg-white disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-500 dark:bg-gray-800',
          {
            'border-gray-100 hover:border-gray-300 disabled:hover:border-gray-100 dark:border-gray-600 dark:hover:border-gray-400 dark:disabled:border-gray-700':
              !active,
            'border-blue-400 dark:border-blue-400': active,
          }
        ),
        rightContent: <Icon name="caretDown" className="shrink-0 text-gray-300 dark:text-gray-400" />,
      }
    case ButtonTheme.dark:
      return {
        theme: 'transparent',
        contentClass: 'font-normal truncate',
        className: classnames('border bg-gray-800 text-gray-50 disabled:border-gray-700 disabled:text-gray-500', {
          'border-gray-600 hover:border-gray-400': !active,
          'border-blue-400': active,
        }),
        rightContent: <Icon name="caretDown" className="shrink-0 text-gray-400" />,
      }
    default:
      return {}
  }
}

export interface SelectButtonProps extends ButtonProps, ButtonHTMLProps {
  /**
   * Theme for the button
   *
   * @default light
   */
  theme?: ButtonThemeType

  isLoading?: boolean

  state?: PopoverStateReturn

  isPopoverTrigger?: boolean
}

export function SelectButton({
  state,
  theme = 'light',
  isPopoverTrigger = true,
  isLoading,
  ...props
}: SelectButtonProps) {
  const themeProps = React.useMemo(() => getButtonProps(ButtonTheme[theme], !!props.active), [theme, props.active])

  return (
    <Button
      {...themeProps}
      {...props}
      as={isPopoverTrigger ? PopoverTrigger : undefined}
      state={state!}
      contentClass={classnames(themeProps.contentClass, props.contentClass)}
      className={classnames(themeProps.className, props.className)}
      type="button"
      theme={themeProps.theme ?? theme}
      rightContent={
        isLoading ? (
          <Delayed delayComponent={props.rightContent ?? themeProps.rightContent}>
            <Spinner width={16} />
          </Delayed>
        ) : (
          props.rightContent ?? themeProps.rightContent
        )
      }
    >
      {props.children}
    </Button>
  )
}
